import { MenuOpen } from '@mui/icons-material';
import { Box, Card, Drawer, Grid, IconButton, Toolbar, Typography } from '@mui/material';
import clsx from 'clsx';
import { AnimationDirection, Contract } from 'components/animations';
import { MenuToggleButton } from 'components/buttons';
import { FlexScroll } from 'components/FlexScroll';
import { LoadingBar } from 'components/LoadingBar';
import { PageSidebar, PageSidebarProps } from 'components/PageSidebar';
import { withLoadingContext } from 'contexts';
import React, { useEffect, useRef, useState } from 'react';

import HeaderPage from '../HeaderPage';
import useCardPageStyles, { useResponsiveStyles } from './styles';
import { CardPageProps } from './types';
import { HeaderTabs } from './HeaderTabs/HeaderTabs';

const defaultSidebarProps: PageSidebarProps = {};

const Root: React.ForwardRefExoticComponent<CardPageProps> = React.forwardRef<
  HTMLDivElement,
  CardPageProps
>((props, ref) => {
  const {
    children,
    headerProps,
    cardHeader,
    cardFooter,
    sidebarOpen = false,
    sidebarProps = defaultSidebarProps,
    extras,
    extrasProps,
    showHeader = true,
    showCardHeader = true,
    showFooter = true,
    flexScrollClassName,
    columnsWrapperClassName = '',
  } = props;
  const classes = useCardPageStyles(props);

  const breakpointRef = useRef(null);
  const responsiveClasses = useResponsiveStyles(breakpointRef);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const [isMenuOpen, setMenuOpen] = useState(!!sidebarOpen);

  useEffect(() => {
    setMenuOpen(sidebarOpen);
  }, [sidebarOpen, setMenuOpen]);

  const renderSidebar = () => {
    if (!sidebarProps.menu && !sidebarProps.content) {
      return null;
    }
    return (
      <Contract isMounted={isMenuOpen} direction={AnimationDirection.TO_LEFT}>
        <div className={responsiveClasses.sidebar}>
          <PageSidebar {...sidebarProps} />
        </div>
      </Contract>
    );
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderMenuToggle = () => {
    return (
      <>
        <div className={responsiveClasses.menuToggleButton}>
          <MenuToggleButton open={isMenuOpen} onClick={toggleMenu} size={'small'} />
        </div>
        <div className={responsiveClasses.responsiveMenuToggleButton}>
          <Box sx={{ paddingTop: 2 }}>
            <IconButton onClick={handleMenuClick} size='small'>
              <MenuOpen />
            </IconButton>
          </Box>
          <Drawer
            id='sidebar-drawer'
            anchor='left'
            open={open}
            onClose={handleClose}
            classes={{ paper: classes.sidePanelDrawer }}
            ModalProps={{ disablePortal: true }}
          >
            <Toolbar className={classes.header}>
              <Grid container>
                <Grid item xs={11}>
                  <Typography variant='h4' className={classes.sidePanelDrawerTitle}>
                    {headerProps.title ?? ''}
                  </Typography>
                </Grid>
                <Grid item xs={1} textAlign={'right'}>
                  <IconButton aria-label='close' size='small' onClick={handleClose}>
                    <MenuOpen sx={{ transform: 'scale(-1, -1)' }} />
                  </IconButton>
                </Grid>
              </Grid>
            </Toolbar>

            <PageSidebar {...sidebarProps} />
          </Drawer>
        </div>
      </>
    );
  };

  const renderExtras = () => {
    const hasExtras = !!extras && extras.length > 0;
    return (
      <Contract
        direction={AnimationDirection.TO_RIGHT}
        duration={extrasProps?.duration}
        isMounted={hasExtras}
        key='extras'
      >
        <div className={classes.extrasWrapper}>
          <FlexScroll>
            <div
              className={clsx(classes.extrasContent, {
                [classes.fullHeightExtras]: extrasProps?.fullHeight,
              })}
            >
              {extras}
            </div>
          </FlexScroll>
        </div>
      </Contract>
    );
  };

  const renderCardPage = () => {
    return (
      <>
        <Box height={'100%'} width={'100%'} ref={breakpointRef}>
          <HeaderPage
            headerProps={{
              expanded: true,
              ...headerProps,
            }}
          >
            <div className={clsx(classes.columnsWrapper, columnsWrapperClassName)}>
              {renderSidebar()}
              <div className={classes.contentWrapper}>
                <Card className={classes.card}>
                  <LoadingBar absolute />
                  {showHeader && (
                    <header className={classes.cardHeader}>
                      {(sidebarProps.menu || sidebarProps.content) && renderMenuToggle()}
                      {showCardHeader && cardHeader}
                    </header>
                  )}
                  <main className={classes.cardContent}>
                    <FlexScroll className={flexScrollClassName} ref={ref}>
                      {children}
                    </FlexScroll>
                  </main>
                  {showFooter && <footer className={classes.cardFooter}>{cardFooter}</footer>}
                </Card>
                {renderExtras()}
              </div>
            </div>
          </HeaderPage>
        </Box>
      </>
    );
  };

  return renderCardPage();
});

type Namespace = {
  HeaderTabs: typeof HeaderTabs;
};

const CardPage = withLoadingContext(React.memo(Root)) as typeof Root & Namespace;

CardPage.HeaderTabs = HeaderTabs;

export default CardPage;
